import {useEffect, useMemo, useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {getBenefitsList, getWorkforcesList} from '../../shared/helpers/common'
import {useConfig} from './config'
import {useUpdateAdminPermission} from '../../AdminPanel/api/updateAdminPermissions'
import {useUpdateCustomLabels} from '../../AdminPanel/api/updateCustomLabels'
import {useGetAdminPermission} from '../../AdminPanel/api/getAdminPermission'
import {usePointOfInterest} from './pointOfInterest'
import {getInitialUseCaseConfig} from '../../AdminPanel/ProjectDetails/Helper/UseCaseHelper'
import {processCustomLabelsResponse} from '../../AdminPanel/customSettings/Customization/helper'
import {
  setRefreshDashboard,
  setAdminPermissionLoading,
  setFilterData,
  setJurisdiction,
  setHealthCondition,
  setStates,
  setCurrentJurisdiction,
  setCurrentDataAggregation,
  setCurrentHealthCondition,
  setCurrentSdoh,
  setCurrentPOI,
  setCurrentBenefits,
  setCurrentWorkforces,
  setUseCaseConfigData,
  setCurrentUseCases,
  setCurrentTrendAnalysisFlag,
  setCurrentUserFeedbackFlag,
  setCurrentChatbotFlag,
  setCurrentChatbotExportFlag,
  setCurrentChatbotCopyFlag,
  setCurrentExportFlag,
  setCurrentPersonaFlag,
  setCustomLabels,
  setCustomizationDefaultData,
  setCustomizationData,
  selectRefreshDashboard,
  selectFilterData,
  selectAdminPermissionLoading,
  selectJurisdiction,
  selectHealthCondition,
  selectSdohData,
  selectHealthAndEquityConditions,
  selectStates,
  selectSdoh,
  selectCurrentJurisdiction,
  selectCurrentDataAggregation,
  selectCurrentHealthCondition,
  selectCurrentSdoh,
  selectCurrentPOI,
  selectCurrentBenefits,
  selectCurrentWorkforces,
  selectUseCaseConfigData,
  selectCurrentUseCases,
  selectCurrentTrendAnalysisFlag,
  selectCurrentUserFeedbackFlag,
  selectCurrentChatbotFlag,
  selectCurrentChatbotExportFlag,
  selectCurrentChatbotCopyFlag,
  selectCurrentExportFlag,
  selectCurrentPersonaFlag,
  selectCustomLabels,
  selectCustomizationDefaultData,
  selectCustomizationData
} from '../slices/adminPermissionSlice'

export const useAdminPermission = () => {
  const dispatch = useDispatch()
  const {dataAggregationConfig, healthAndEquityConfig, sdohConfig, filterConfig, useCasesConfig} = useConfig() || {}
  // Redux state selectors
  const refreshDashboard = useSelector(selectRefreshDashboard)
  const filterDataFromRedux = useSelector(selectFilterData)
  const jurisdiction = useSelector(selectJurisdiction)
  const healthCondition = useSelector(selectHealthCondition)
  const sdohData = useSelector(selectSdohData)
  const healthAndEquityConditions = useSelector(selectHealthAndEquityConditions)
  const states = useSelector(selectStates)
  const sdoh = useSelector(selectSdoh)
  const currentJurisdiction = useSelector(selectCurrentJurisdiction)
  const currentDataAggregation = useSelector(selectCurrentDataAggregation)
  const currentHealthCondition = useSelector(selectCurrentHealthCondition)
  const currentSdoh = useSelector(selectCurrentSdoh)
  const currentPOI = useSelector(selectCurrentPOI)
  const currentBenefits = useSelector(selectCurrentBenefits)
  const currentWorkforces = useSelector(selectCurrentWorkforces)
  const useCaseConfigData = useSelector(selectUseCaseConfigData)
  const currentUseCases = useSelector(selectCurrentUseCases)
  const currentTrendAnalysisFlag = useSelector(selectCurrentTrendAnalysisFlag)
  const currentUserFeedbackFlag = useSelector(selectCurrentUserFeedbackFlag)
  const currentChatbotFlag = useSelector(selectCurrentChatbotFlag)
  const currentChatbotExportFlag = useSelector(selectCurrentChatbotExportFlag)
  const currentChatbotCopyFlag = useSelector(selectCurrentChatbotCopyFlag)
  const currentExportFlag = useSelector(selectCurrentExportFlag)
  const currentPersonaFlag = useSelector(selectCurrentPersonaFlag)
  const customLabels = useSelector(selectCustomLabels)
  const customizationDefaultData = useSelector(selectCustomizationDefaultData)
  const customizationData = useSelector(selectCustomizationData)
  const filterDetailsLoading = useSelector(selectAdminPermissionLoading)

  // API calls
  const {
    adminPermissionLoading: filterDetailsLoadingFromQuery,
    adminPermissionError: filterDetailsError,
    data: filterData,
    filters: filtersFromAPI,
    sdohFilter,
    healthConditionFilter,
    stateListFilter,
    pointOfInterestFilter,
    permissionList,
    refetchAdminPermission: refetchFilterDetailsList,
    isQueryActive
  } = useGetAdminPermission({entireUnitedStateLabel: 'Entire United States', totalStates: 51})

  // Update Redux with loading state - ONLY dispatch FALSE when data is loaded
  // Never dispatch TRUE - Redux initialState already has adminPermissionLoading: true
  useEffect(() => {
    if (isQueryActive && !filterDetailsLoading) {
      dispatch(setAdminPermissionLoading(false))
    }
  }, [filterDetailsLoading, isQueryActive, dispatch])

  const {pointOfInterests} = usePointOfInterest()

  const pointOfInterestsData = useMemo(
    () =>
      pointOfInterests?.map((poiValue) => ({
        title: poiValue.label,
        POI_ID: poiValue.poi_id,
        IS_ACTIVE: poiValue.enabled
      })) || [],
    [pointOfInterests]
  )

  const benefits = getBenefitsList()
  const workforces = getWorkforcesList()

  const [
    updateAdminPermissions,
    {data: updatePermissionList, loading: loadingUpdatedPermission, error: errorUpdatedPermission}
  ] = useUpdateAdminPermission()
  const [updateCustomizations, {data: updatedLabelList, loading: updatedLabelLoading, error: updatedLabelError}] =
    useUpdateCustomLabels()

  // Effect to update states from API data
  // Update Redux loading state when data becomes available
  useEffect(() => {
    if (!filterDetailsLoadingFromQuery && filterData?.adminPermission && filterDetailsLoading) {
      dispatch(setAdminPermissionLoading(false))
    }
  }, [filterDetailsLoadingFromQuery, filterData, filterDetailsLoading, dispatch])

  // Effect to update healthAndEquityConditions, states, and sdoh from API data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!filterDetailsLoading && filterData?.adminPermission) {
      // Store filterData in Redux
      dispatch(setFilterData(filtersFromAPI))

      const newStateList = (filterData.adminPermission.stateList || []).filter((item) => item.STATE_ID !== 0)
      // Only dispatch setStates when the actual state IDs change to avoid unnecessary re-renders
      const newStateKeys = newStateList
        .map((s) => s.STATE_ID)
        .sort()
        .join(',')
      const currentStateKeys = states.stateList
        .map((s) => s.STATE_ID)
        .sort()
        .join(',')
      if (newStateKeys !== currentStateKeys) {
        dispatch(setStates({stateList: newStateList}))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDetailsLoading, filterData, dispatch])

  // Effect to update jurisdiction from states
  // Guard dispatch to avoid new array references when data hasn't changed
  useEffect(() => {
    if (states.stateList.length > 0) {
      const jurisdictionData = states.stateList.map(({STATE_NAME: title, ...rest}) => ({title, ...rest}))
      const newKeys = jurisdictionData
        .map((j) => j.STATE_ID)
        .sort()
        .join(',')
      const currentKeys = jurisdiction
        .map((j) => j.STATE_ID)
        .sort()
        .join(',')
      if (newKeys !== currentKeys) {
        dispatch(setJurisdiction(jurisdictionData))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states, dispatch])

  // SDOH data is no longer fetched from API - using config files instead

  // Helper to set flag state with fallback
  const setFlagState = (action, flag, fallback = [0]) => {
    if (Array.isArray(flag) && flag.length > 0) {
      dispatch(action(flag))
    } else {
      dispatch(action(fallback))
    }
  }

  // Helper function to check if permissions should be updated
  const shouldUpdatePermissions = () => {
    return !filterDetailsLoading && !filterDetailsError && Object.keys(permissionList || {}).length > 0
  }

  // Effect to update permissions, health condition, and use case config
  useEffect(() => {
    if (shouldUpdatePermissions()) {
      // Update use case configuration
      dispatch(
        setUseCaseConfigData(getInitialUseCaseConfig(useCasesConfig?.data, permissionList?.usecasePermissions || []))
      )

      // Update health condition
      dispatch(
        setHealthCondition(
          healthAndEquityConditions.healthEquityConditionList.healthEquityConditionList.map(
            ({HEALTH_EQUITY_CONDITION_NAME: title, ...rest}) => ({title, ...rest})
          )
        )
      )

      // Update permission flags
      dispatch(setCurrentBenefits(permissionList?.benefitsEligibilityPermissions || []))
      dispatch(setCurrentWorkforces(permissionList?.workforceRecruitmentPermissions || []))
      dispatch(setCurrentUseCases(permissionList?.usecasePermissions || []))
      dispatch(
        setCurrentUserFeedbackFlag(
          permissionList?.userfeedbackFlagPermissions && permissionList?.userfeedbackFlagPermissions.length > 0
            ? permissionList?.userfeedbackFlagPermissions
            : [0]
        )
      )
      dispatch(setCurrentDataAggregation(permissionList?.dataAggregationPermissions || []))
      setFlagState(setCurrentTrendAnalysisFlag, permissionList?.trendAnalysisPermissions)
      setFlagState(setCurrentChatbotFlag, permissionList?.chatbotFlagPermissions)
      setFlagState(setCurrentChatbotExportFlag, permissionList?.chatbotExportFlagPermissions)
      setFlagState(setCurrentChatbotCopyFlag, permissionList?.chatbotCopyFlagPermissions)
      setFlagState(setCurrentExportFlag, permissionList?.exportFlagPermissions)
      setFlagState(setCurrentPersonaFlag, permissionList?.personaFlagPermissions)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterDetailsLoading,
    filterDetailsError,
    healthAndEquityConditions,
    loadingUpdatedPermission,
    permissionList,
    dispatch
  ])

  // Effect to update current permissions and customization data
  useEffect(() => {
    if (!filterDetailsLoading && !filterDetailsError && filterData?.adminPermission) {
      const {healthEquityConditionList, sdohList, stateList, pointOfInterestList} = filterData.adminPermission
      dispatch(
        setCurrentHealthCondition(
          healthEquityConditionList?.data
            ?.filter(({IS_PERMISSIONED}) => IS_PERMISSIONED)
            .map(({HEALTH_EQUITY_CONDITION_ID}) => HEALTH_EQUITY_CONDITION_ID) || []
        )
      )
      dispatch(
        setCurrentJurisdiction(
          stateList
            ?.filter(({STATE_ID, IS_PERMISSIONED}) => IS_PERMISSIONED && STATE_ID !== 0)
            .map(({STATE_ID}) => STATE_ID) || []
        )
      )
      dispatch(
        setCurrentSdoh(
          sdohList?.data?.filter(({IS_PERMISSIONED}) => IS_PERMISSIONED).map(({FILTER_ID}) => FILTER_ID) || []
        )
      )
      dispatch(
        setCurrentPOI(
          pointOfInterestList?.filter(({IS_PERMISSIONED}) => IS_PERMISSIONED).map(({POI_ID}) => POI_ID) || []
        )
      )
      const custData = processCustomLabelsResponse(filterData.adminPermission, filterConfig)
      dispatch(setCustomizationDefaultData(custData))
      dispatch(setCustomizationData(custData))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterDetailsLoading,
    filterDetailsError,
    loadingUpdatedPermission,
    filterData,
    healthAndEquityConfig,
    sdohConfig,
    dispatch
  ])

  const reloadFilters = useCallback(() => {
    refetchFilterDetailsList()
  }, [refetchFilterDetailsList])

  const updateCustomizationLabels = useCallback(
    (customLabels) => {
      updateCustomizations({variables: {customLabels}})
    },
    [updateCustomizations]
  )

  const updateCustomizationWithDefaultData = useCallback(() => {
    dispatch(setCustomizationData(customizationDefaultData))
  }, [customizationDefaultData, dispatch])

  // Action dispatchers
  const setCurrentBenefitsAction = useCallback((value) => dispatch(setCurrentBenefits(value)), [dispatch])
  const setCurrentHealthConditionAction = useCallback((value) => dispatch(setCurrentHealthCondition(value)), [dispatch])
  const setCurrentJurisdictionAction = useCallback((value) => dispatch(setCurrentJurisdiction(value)), [dispatch])
  const setCurrentSdohAction = useCallback((value) => dispatch(setCurrentSdoh(value)), [dispatch])
  const setCurrentWorkforcesAction = useCallback((value) => dispatch(setCurrentWorkforces(value)), [dispatch])
  const setCurrentUseCasesAction = useCallback((value) => dispatch(setCurrentUseCases(value)), [dispatch])
  const setCurrentPOIAction = useCallback((value) => dispatch(setCurrentPOI(value)), [dispatch])
  const setCurrentTrendAnalysisFlagAction = useCallback(
    (value) => dispatch(setCurrentTrendAnalysisFlag(value)),
    [dispatch]
  )
  const setCurrentUserFeedbackFlagAction = useCallback(
    (value) => dispatch(setCurrentUserFeedbackFlag(value)),
    [dispatch]
  )
  const setCurrentChatbotFlagAction = useCallback((value) => dispatch(setCurrentChatbotFlag(value)), [dispatch])
  const setCurrentChatbotExportFlagAction = useCallback(
    (value) => dispatch(setCurrentChatbotExportFlag(value)),
    [dispatch]
  )
  const setCurrentChatbotCopyFlagAction = useCallback((value) => dispatch(setCurrentChatbotCopyFlag(value)), [dispatch])
  const setCurrentExportFlagAction = useCallback((value) => dispatch(setCurrentExportFlag(value)), [dispatch])
  const setCurrentPersonaFlagAction = useCallback((value) => dispatch(setCurrentPersonaFlag(value)), [dispatch])
  const setCustomLabelsAction = useCallback((value) => dispatch(setCustomLabels(value)), [dispatch])
  const setCustomizationDataAction = useCallback((value) => dispatch(setCustomizationData(value)), [dispatch])
  const setRefreshDashboardAction = useCallback((value) => dispatch(setRefreshDashboard(value)), [dispatch])

  return {
    filterDataFromRedux,
    currentBenefits,
    currentHealthCondition,
    currentJurisdiction,
    currentSdoh,
    currentWorkforces,
    currentUseCases,
    currentPOI,
    currentTrendAnalysisFlag,
    currentUserFeedbackFlag,
    currentChatbotFlag,
    currentChatbotExportFlag,
    currentChatbotCopyFlag,
    currentExportFlag,
    currentDataAggregation,
    currentPersonaFlag,
    setCurrentBenefits: setCurrentBenefitsAction,
    setCurrentHealthCondition: setCurrentHealthConditionAction,
    setCurrentJurisdiction: setCurrentJurisdictionAction,
    setCurrentSdoh: setCurrentSdohAction,
    setCurrentWorkforces: setCurrentWorkforcesAction,
    setCurrentUseCases: setCurrentUseCasesAction,
    setCurrentPOI: setCurrentPOIAction,
    setCurrentTrendAnalysisFlag: setCurrentTrendAnalysisFlagAction,
    setCurrentUserFeedbackFlag: setCurrentUserFeedbackFlagAction,
    setCurrentChatbotFlag: setCurrentChatbotFlagAction,
    setCurrentChatbotExportFlag: setCurrentChatbotExportFlagAction,
    setCurrentChatbotCopyFlag: setCurrentChatbotCopyFlagAction,
    setCurrentExportFlag: setCurrentExportFlagAction,
    setCurrentPersonaFlag: setCurrentPersonaFlagAction,
    refetchFilterDetailsList,
    filterDetailsLoading,
    filterDetailsError,
    filters: filtersFromAPI,
    sdohFilter,
    filterData,
    healthConditionFilter,
    stateListFilter,
    pointOfInterestFilter,
    states,
    benefits,
    workforces,
    useCaseConfigData,
    dataAggregationConfig,
    healthCondition,
    sdohData,
    jurisdiction,
    pointOfInterestsData,
    updateAdminPermissions,
    updatePermissionList,
    loadingUpdatedPermission,
    errorUpdatedPermission,
    updateCustomizations,
    updatedLabelList,
    updatedLabelLoading,
    updatedLabelError,
    reloadFilters,
    customLabels,
    setCustomLabels: setCustomLabelsAction,
    updateCustomizationLabels,
    customizationData,
    setCustomizationData: setCustomizationDataAction,
    refreshDashboard,
    setRefreshDashboard: setRefreshDashboardAction,
    updateCustomizationWithDefaultData,
    healthAndEquityConditions,
    sdoh
  }
}
